import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DollarSign, Info, AlertCircle, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { useQuery } from '@tanstack/react-query'
import { resourcesApi } from '@/shared/api/resources'

export default function ResourcePrice() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  // Fetch pricing tiers to check eligibility
  const { data: pricingData, isLoading: isPricingLoading } = useQuery({
    queryKey: ['pricingTiers', resourceId],
    queryFn: () => resourcesApi.getPricingTiers(resourceId!),
    enabled: !!resourceId,
  })

  const [baseReward, setBaseReward] = useState<number>(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      setBaseReward(resource.base_reward || 0)
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const handleSave = async () => {
    if (!resource) return

    // Validate
    if (baseReward < 0 || baseReward > 100) {
      setError('Цена должна быть от 0 до 100 рублей')
      hapticNotification('error')
      return
    }

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          base_reward: baseReward,
          premium_reward: baseReward, // Set premium to same as base
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update price:', error)
      setError('Ошибка при сохранении цены')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)

    // Check if price changes are allowed
    const isEligible = pricingData?.is_eligible ?? true

    // Only show save button if eligible
    if (isEligible) {
      showMainButton('Сохранить цену', onSave, '#5288c1', '#ffffff')
    } else {
      hideMainButton()
    }

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading, pricingData])

  if (isLoading || isPricingLoading || !resource || !pricingData) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  const presetValues = [1.0, 1.5, 2.0, 2.5, 3.0]
  const isEligible = pricingData?.is_eligible ?? true

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Ценообразование</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {/* Not Eligible Warning */}
        {!isEligible && (
          <div className="bg-red-600/10 border-2 border-red-600/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-600 mb-2">Изменение цены недоступно</h3>
                <p className="text-sm text-red-400 leading-relaxed">
                  Настройка цен доступна только для ресурсов без фильтров, за исключением фильтров не меняющих цену.
                  Должен стоять фильтр на конкретный язык: RU / EN
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Base Reward Card */}
        <div
          className="rounded-xl p-4 space-y-3 border-2 transition-all"
          style={{
            borderColor: '#3b82f633',
            backgroundColor: '#3b82f60d'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#3b82f61a' }}
              >
                <DollarSign className="w-5 h-5 text-[#3b82f6]" />
              </div>
              <div>
                <div className="font-semibold text-tg-text">Базовая цена</div>
                <div className="text-xs text-tg-hint">Для обычных пользователей</div>
              </div>
            </div>
            <div className="text-xl font-bold text-[#3b82f6]">
              {baseReward.toFixed(2)} ₽
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-2">
            <input
              type="range"
              min={0}
              max={10}
              step={0.1}
              value={baseReward}
              onChange={(e) => {
                setBaseReward(parseFloat(e.target.value))
                setError('')
              }}
              onTouchStart={() => hapticFeedback('light')}
              disabled={!isEligible}
              className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(baseReward / 10) * 100}%, rgba(168, 179, 207, 0.2) ${(baseReward / 10) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                ['--slider-color' as any]: '#3b82f6'
              }}
            />
            <div className="flex justify-between text-xs text-tg-hint">
              <span>0₽</span>
              <span>10₽</span>
            </div>
          </div>

          {/* Preset Buttons */}
          <div className="flex gap-1.5">
            {presetValues.map((val) => (
              <button
                key={val}
                onClick={() => {
                  hapticFeedback('light')
                  setBaseReward(val)
                  setError('')
                }}
                disabled={!isEligible}
                className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={
                  Math.abs(baseReward - val) < 0.01
                    ? { backgroundColor: '#3b82f6', color: '#ffffff' }
                    : { backgroundColor: '#3b82f61a', color: '#3b82f6' }
                }
              >
                {val}₽
              </button>
            ))}
          </div>

          {/* Number Input */}
          <div className="relative">
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={baseReward}
              onChange={(e) => {
                const value = parseFloat(e.target.value)
                if (!isNaN(value)) {
                  setBaseReward(value)
                  setError('')
                }
              }}
              onFocus={() => hapticFeedback('light')}
              disabled={!isEligible}
              className="w-full px-4 py-2.5 text-sm font-medium text-center border-2 rounded-lg focus:outline-none transition-colors bg-tg-bg text-tg-text disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                borderColor: '#3b82f633'
              }}
              placeholder="Введите цену"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-tg-hint">
              ₽
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-600/10 border-2 border-red-600/20 rounded-xl">
            <p className="text-sm text-red-600 text-center font-medium">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Подробнее о ценообразовании</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Установите цену за подписчика. Эта цена будет применяться для всех пользователей, выполняющих задачу.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
